import { alpha } from '@mui/material/styles';
import {
    APP_STYLE_COMPONENT_COLORS as MSI_COMPONENT_COLORS,
    MSI_GEOMETRY,
    APP_STYLE_PALETTE as MSI_PALETTE,
    MSI_TYPE_SCALE,
    usesAppStyleGrammar,
} from './msiStyleTokens';
import { workspacePanelElevationSx } from './workspaceChromeStyles';

export const analyticsPanelSx = {
    ...workspacePanelElevationSx,
    borderColor: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.line : undefined),
    borderRadius: (theme) => (
        usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.panel}px` : undefined
    ),
    backgroundColor: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.surface : undefined),
    backgroundImage: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
};

export const analyticsInsetPanelSx = {
    borderColor: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.line : undefined),
    borderRadius: (theme) => (
        usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.panel}px` : undefined
    ),
    backgroundColor: (theme) => (
        usesAppStyleGrammar(theme) ? MSI_PALETTE.surfaceSubtle : undefined
    ),
    backgroundImage: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
    boxShadow: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
};

export const analyticsSecondaryControlSx = {
    borderColor: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.lineDark : undefined),
    borderRadius: (theme) => (
        usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.button}px` : undefined
    ),
    backgroundColor: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.surface : undefined),
    color: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.ink700 : undefined),
    letterSpacing: (theme) => (usesAppStyleGrammar(theme) ? 0 : undefined),
    boxShadow: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
};

export const analyticsCompactBadgeSx = {
    borderRadius: (theme) => (
        usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.statusBadge}px` : undefined
    ),
    backgroundColor: (theme) => (
        usesAppStyleGrammar(theme) ? MSI_PALETTE.surfaceSubtle : undefined
    ),
    color: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.ink500 : undefined),
    fontSize: (theme) => (
        usesAppStyleGrammar(theme) ? MSI_TYPE_SCALE.statusBadge.fontSize : undefined
    ),
    fontWeight: (theme) => (
        usesAppStyleGrammar(theme) ? MSI_TYPE_SCALE.statusBadge.fontWeight : undefined
    ),
    letterSpacing: (theme) => (usesAppStyleGrammar(theme) ? 0 : undefined),
    textTransform: (theme) => (usesAppStyleGrammar(theme) ? 'uppercase' : undefined),
};

export const analyticsCompactLabelSx = {
    color: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.ink500 : undefined),
    fontSize: (theme) => (
        usesAppStyleGrammar(theme) ? MSI_TYPE_SCALE.tableHeader.fontSize : undefined
    ),
    fontWeight: (theme) => (
        usesAppStyleGrammar(theme) ? MSI_TYPE_SCALE.tableHeader.fontWeight : undefined
    ),
    letterSpacing: (theme) => (usesAppStyleGrammar(theme) ? 0 : undefined),
    textTransform: (theme) => (usesAppStyleGrammar(theme) ? 'uppercase' : undefined),
};

export const analyticsToggleGroupSx = {
    borderRadius: (theme) => (
        usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.button}px` : undefined
    ),
    '& .MuiToggleButton-root': {
        borderColor: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.lineDark : undefined),
        backgroundColor: (theme) => (
            usesAppStyleGrammar(theme) ? MSI_PALETTE.surface : undefined
        ),
        color: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.ink500 : undefined),
        letterSpacing: (theme) => (usesAppStyleGrammar(theme) ? 0 : undefined),
        '&.Mui-selected, &.Mui-selected:hover': {
            borderColor: (theme) => (
                usesAppStyleGrammar(theme) ? MSI_COMPONENT_COLORS.focusBorder : undefined
            ),
            backgroundColor: (theme) => (
                usesAppStyleGrammar(theme) ? MSI_PALETTE.blue100 : undefined
            ),
            color: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.ink900 : undefined),
        },
    },
    '& .MuiToggleButtonGroup-grouped:first-of-type': {
        borderTopLeftRadius: (theme) => (
            usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.button}px` : undefined
        ),
        borderBottomLeftRadius: (theme) => (
            usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.button}px` : undefined
        ),
    },
    '& .MuiToggleButtonGroup-grouped:last-of-type': {
        borderTopRightRadius: (theme) => (
            usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.button}px` : undefined
        ),
        borderBottomRightRadius: (theme) => (
            usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.button}px` : undefined
        ),
    },
};

export const analyticsActionButtonSx = {
    height: (theme) => (
        usesAppStyleGrammar(theme) ? MSI_GEOMETRY.controlHeight : undefined
    ),
    borderRadius: (theme) => (
        usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.button}px` : undefined
    ),
};

export const analyticsDirtyIconSx = {
    '&:focus-visible': {
        outline: (theme) => (
            usesAppStyleGrammar(theme)
                ? `2px solid ${MSI_COMPONENT_COLORS.focusBorder}`
                : undefined
        ),
        outlineOffset: 2,
    },
};

export const getAnalyticsDirtyIconColor = (theme, fallbackColor) => (
    usesAppStyleGrammar(theme) ? MSI_PALETTE.warning : fallbackColor
);

export const getAnalyticsPrimarySeriesColor = (theme, fallbackColor) => (
    usesAppStyleGrammar(theme) ? MSI_PALETTE.blue600 : fallbackColor
);

export function getAnalyticsUnsavedControlStyle(theme, fallbackBackground, attention = false) {
    if (!usesAppStyleGrammar(theme)) {
        return {
            backgroundColor: fallbackBackground,
            borderRadius: 1,
        };
    }
    return {
        backgroundColor: attention ? MSI_PALETTE.warningBg : MSI_PALETTE.surface,
        borderRadius: `${MSI_GEOMETRY.radii.input}px`,
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: attention
                ? MSI_COMPONENT_COLORS.semanticBorders.warning
                : MSI_PALETTE.lineDark,
        },
    };
}

export const analyticsTooltipSx = {
    border: (theme) => (
        usesAppStyleGrammar(theme) ? `1px solid ${MSI_PALETTE.line}` : undefined
    ),
    borderRadius: (theme) => (
        usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.panel}px` : undefined
    ),
    backgroundColor: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.surface : undefined),
    boxShadow: (theme) => (
        usesAppStyleGrammar(theme) ? '0 8px 24px rgba(12, 23, 38, 0.12)' : undefined
    ),
};

export function getAnalyticsChartChrome(theme) {
    if (!usesAppStyleGrammar(theme)) {
        return {
            grid: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.16)
                : alpha(theme.palette.divider, 0.8),
            axis: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.52)
                : theme.palette.divider,
            reference: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.42)
                : theme.palette.text.disabled,
            text: theme.palette.mode === 'dark'
                ? theme.palette.common.white
                : theme.palette.text.secondary,
            tickFontSize: 12,
        };
    }

    return {
        grid: MSI_COMPONENT_COLORS.rowDivider,
        axis: MSI_PALETTE.lineDark,
        reference: MSI_PALETTE.ink400,
        text: MSI_PALETTE.ink500,
        tickFontSize: MSI_TYPE_SCALE.helper.fontSize,
    };
}

export const analyticsDrawerPaperSx = {
    borderLeft: (theme) => (
        usesAppStyleGrammar(theme) ? `1px solid ${MSI_PALETTE.line}` : undefined
    ),
    borderRadius: (theme) => (usesAppStyleGrammar(theme) ? 0 : undefined),
    backgroundColor: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.surface : undefined),
    backgroundImage: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
    boxShadow: (theme) => (
        usesAppStyleGrammar(theme) ? '-8px 0 24px rgba(12, 23, 38, 0.10)' : undefined
    ),
};

export const analyticsMenuPaperSx = {
    borderColor: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.line : undefined),
    borderRadius: (theme) => (
        usesAppStyleGrammar(theme) ? `${MSI_GEOMETRY.radii.panel}px` : undefined
    ),
    backgroundColor: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.surface : undefined),
    backgroundImage: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
    boxShadow: (theme) => (
        usesAppStyleGrammar(theme) ? '0 8px 24px rgba(12, 23, 38, 0.12)' : undefined
    ),
    '&::before, &::after': {
        background: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.surface : undefined),
        letterSpacing: (theme) => (usesAppStyleGrammar(theme) ? 0 : undefined),
    },
};
